import ConfirmModal from "@app/components/confirm-modal";
import RejectReasonModal from "@app/components/reject-reason-modal";
import Table, {
  ColumnConfig,
  Order,
} from "@app/components/table/table.component";
import TitleDescription from "@app/components/title-description";
import { addToast } from "@app/components/toast/toast.service";
import {
  DEBOUNCE_TIMER,
  DEFAULT_PAGE,
  DEFAULT_REQUEST_PARAMS,
  OrderType,
  RoutePath,
  SystemMessage,
  TransactionOption,
  TransactionOptionName,
} from "@app/constants";
import BankTransferStatementService from "@app/services/http/bank-transfer-statement.service";
import TransferHistoryService from "@app/services/http/transfer-history.service";
import { openPortalDialog } from "@app/services/modal.service";
import { Meta } from "@app/types";
import { TransferHistory } from "@app/types/transfer-history.type";
import { formatDateTime, formatMoneyVN } from "@app/utils/util";
import useCancellationRequest from "@core/hooks/use-cancellation-request.hook";
import { useDebouncedCallback } from "@core/hooks/use-debounced-callback.hook";
import useForceUpdate from "@core/hooks/use-force-update.hook";
import { RequestParams } from "@core/services/http/http.type";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

function OptionColumnTable({ data }: { data: TransferHistory }) {
  return (
    <div>
      <span
        className={clsx("px-4 py-2 rounded-lg text-xs", {
          "bg-[#E8FFF4] text-[#00A653]":
            data.status === TransactionOption.SUCCESS,
          "bg-[#FFF0E6] text-[#FE7E20]":
            data.status === TransactionOption.TIMEOUT,
          "bg-[#CCE7FF] text-[#4558FF]":
            data.status === TransactionOption.PROCESSING,
          "bg-[#FFEBEB] text-[#FC4040]":
            data.status === TransactionOption.ERROR ||
            data.status === TransactionOption.REJECTED,
        })}
      >
        {TransactionOptionName[data.status]}
      </span>
    </div>
  );
}

function AuthorNameColumnTable({ data }: { data: TransferHistory }) {
  return <div>{data.authorName}</div>;
}

function CreatedDateColumnTable({ data }: { data: TransferHistory }) {
  return <div>{formatDateTime(data.createdDate)}</div>;
}

function AmountColumnTable({ data }: { data: TransferHistory }) {
  return <div>{formatMoneyVN(data.amount)} đ</div>;
}

function RemainingAmountColumnTable({ data }: { data: TransferHistory }) {
  return <div>{formatMoneyVN(data.remainingAmount)} đ</div>;
}

function BankColumnTable({ data }: { data: TransferHistory }) {
  return <div>{data.bank}</div>;
}

function ActionColumnTable({
  data,
  customProps,
}: {
  data: TransferHistory;
  customProps?: {
    onConfirm?: ({ data }: { data: TransferHistory }) => void;
    onReject?: (id: number) => void;
    onAddToStatement?: (id: number) => void;
  };
}) {
  const handleConfirmClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    customProps?.onConfirm && customProps.onConfirm({ data });
  };

  const handleRejectClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    customProps?.onReject && customProps.onReject(data.id);
  };

  const handleAddToStatementClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    customProps?.onAddToStatement && customProps.onAddToStatement(data.id);
  };

  const isProcessing = data.status === TransactionOption.PROCESSING;
  const isApproved = data.status === TransactionOption.SUCCESS;
  const isRejected = data.status === TransactionOption.ERROR;
  const isLocked = data.status === TransactionOption.LOCKED;
  const canAddToStatement = isProcessing && !isLocked;

  // Hide all buttons if already approved or rejected
  if (isApproved || isRejected) {
    return <div className="text-center text-gray-400">-</div>;
  }

  return (
    <div className="flex gap-2 justify-center items-center flex-wrap">
      {isProcessing && (
        <>
          <div
            className="bg-[#5570F1] hover:bg-[#3557ff] select-none text-white rounded-lg cursor-pointer px-4 py-2 text-sm whitespace-nowrap"
            onClick={handleConfirmClick}
          >
            Xác nhận
          </div>
          <div
            className="bg-[#FC4040] hover:bg-[#e63939] select-none text-white rounded-lg cursor-pointer px-4 py-2 text-sm whitespace-nowrap"
            onClick={handleRejectClick}
          >
            Từ chối
          </div>
          {canAddToStatement && (
            <div
              className="bg-[#00A653] hover:bg-[#008f47] select-none text-white rounded-lg cursor-pointer px-4 py-2 text-sm whitespace-nowrap"
              onClick={handleAddToStatementClick}
            >
              Thêm bảng kê
            </div>
          )}
        </>
      )}
    </div>
  );
}

const columnConfig: ColumnConfig[] = [
  {
    label: "Tác giả",
    dataKey: "authorName",
    width: "240px",
    component: AuthorNameColumnTable,
    hasOrder: true,
  },
  {
    label: "Thời gian",
    dataKey: "createdDate",
    width: "200px",
    component: CreatedDateColumnTable,
    hasOrder: true,
  },
  {
    label: "Số tiền rút",
    dataKey: "amount",
    width: "220px",
    component: AmountColumnTable,
    hasOrder: true,
  },
  {
    label: "Số dư còn lại",
    dataKey: "remainingAmount",
    width: "220px",
    component: RemainingAmountColumnTable,
    hasOrder: true,
  },
  {
    label: "Số tài khoản nhận",
    dataKey: "bank",
    width: "220px",
    component: BankColumnTable,
    hasOrder: true,
  },
  {
    label: "Trạng thái",
    dataKey: "status",
    component: OptionColumnTable,
    width: "130px",
    theadClassName: "min-w-[130px]",
    hasOrder: true,
  },
  {
    label: "Hành động",
    dataKey: "action",
    component: ActionColumnTable,
    width: "300px",
    theadClassName: "min-w-[300px]",
  },
];

function TransferHistoryManagement() {
  const [transferHistories, setTransferHistories] = useState<TransferHistory[]>(
    []
  );
  const [requestParams, setRequestParams] = useState<RequestParams>(
    DEFAULT_REQUEST_PARAMS
  );

  const [meta, setMeta] = useState<Meta>();

  const [update, forceUpdate] = useForceUpdate();

  const { abortController } = useCancellationRequest();

  const navigate = useNavigate();

  useEffect(() => {
    TransferHistoryService.getList(requestParams, abortController.signal).then(
      (res) => {
        setTransferHistories(res.data.items);
        setMeta(res.data.meta);

        if (
          requestParams.pageNum &&
          res.data.meta.totalPage > 0 &&
          res.data.meta.totalPage < requestParams.pageNum
        ) {
          const newRequestParams: RequestParams = {
            ...requestParams,
            pageNum: DEFAULT_PAGE,
          };

          setRequestParams(newRequestParams);
        }
      }
    );
  }, [update, requestParams]);

  const onSearchChange = useDebouncedCallback((value: string) => {
    const newRequestParams: RequestParams = {
      ...requestParams,
      keyword: value,
    };

    setRequestParams(newRequestParams);
  }, DEBOUNCE_TIMER);

  const onPageChange = (page: number) => {
    const newRequestParams: RequestParams = { ...requestParams, pageNum: page };

    setRequestParams(newRequestParams);
  };

  const onPageSizeChange = (pageSize: number) => {
    const newRequestParams: RequestParams = { ...requestParams, pageSize };

    setRequestParams(newRequestParams);
  };

  const onOrderChange = (data?: Order) => {
    if (data) {
      const newRequestParams: RequestParams = {
        ...requestParams,
        sortBy: data.orderBy,
        sortType: data.order as OrderType,
      };

      setRequestParams(newRequestParams);
    }
  };

  const handleConfirmClick = ({
    data: transaction,
  }: {
    data?: TransferHistory;
  }) => {
    if (transaction?.id) {
      const confirmModalObs = openPortalDialog(ConfirmModal, {
        message: SystemMessage.CONFIRM_TRANSACTION_NOTIFY,
      });

      confirmModalObs.afterClosed().subscribe((data) => {
        if (data?.isAccept) {
          TransferHistoryService.confirm(transaction.id).then(() => {
            addToast({ text: SystemMessage.CONFIRM_SUCCESSFULLY });
            forceUpdate();
          });
        }
      });
    }
  };

  const handleRejectClick = (id: number) => {
    const rejectModalObs = openPortalDialog(RejectReasonModal, {});

    rejectModalObs.afterClosed().subscribe((data) => {
      if (data?.reason) {
        TransferHistoryService.reject(id, data.reason).then(() => {
          addToast({ text: "Từ chối yêu cầu rút tiền thành công" });
          forceUpdate();
        });
      }
    });
  };

  const handleAddToStatement = (id: number) => {
    const confirmModalObs = openPortalDialog(ConfirmModal, {
      message: "Bạn có chắc chắn muốn thêm yêu cầu này vào bảng kê?",
    });

    confirmModalObs.afterClosed().subscribe((data) => {
      if (data?.isAccept) {
        BankTransferStatementService.create({ transferHistoryId: id })
          .then(() => {
            addToast({ text: "Thêm vào bảng kê thành công" });
            forceUpdate();
          })
          .catch(() => {
            addToast({ 
              text: "Thêm vào bảng kê thất bại", 
              status: "inValid" 
            });
          });
      }
    });
  };

  return (
    <div className="py-6 px-12">
      <div className="flex justify-between items-center">
        <div className="text-[#343C6A]">Lịch sử rút tiền</div>
        <div>
          <button
            className="text-xl px-6 rounded-xl leading-14 h-14 bg-color-primary hover:bg-[#667ffc] text-white"
            onClick={() => navigate(`/${RoutePath.BANK_TRANSFER_STATEMENT_MANAGEMENT}`)}
          >
            <div className="flex gap-4">
              <div>Xem bảng kê</div>
            </div>
          </button>
        </div>
      </div>
      <div className="mt-6 rounded-xl bg-white py-6 px-5">
        <TitleDescription
          title="Lịch Sử Rút Tiền"
          onInputChange={onSearchChange}
        />
        <div className="mt-6 overflow-x-auto">
          <Table
            columnConfig={columnConfig}
            dataTable={transferHistories}
            meta={meta}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            onRowClickOrder={onOrderChange}
            customProps={{
              onConfirm: handleConfirmClick,
              onReject: handleRejectClick,
              onAddToStatement: handleAddToStatement,
            }}
            componentEmpty={
              <div className="text-center py-3 bg-[#f4f4f4]">
                <p className="text-[#666] text-xl">Không có dữ liệu</p>
              </div>
            }
          />
        </div>
      </div>
    </div>
  );
}

export default TransferHistoryManagement;
